// src/utils/rangeFormatters.js - Updated

/**
 * Formats the title for a range view based on position, bet type, and context
 * 
 * @param {string} position - Hero position (e.g., "BB", "BTN")
 * @param {string} betType - Type of betting action (e.g., "OPEN", "FACING_OPEN_3BET")
 * @param {Object} options - Additional context options
 * @param {string} options.openPosition - Position of the opener (e.g., "BTN")
 * @param {string} options.threeBetPosition - Position of the 3bettor
 * @param {string} options.mappedBetType - Alternative bet type display text
 * @param {string} options.callType - Type of call (if applicable)
 * @returns {string} Formatted title
 */
export const formatTitle = (position, betType, options = {}) => {
  const { openPosition, threeBetPosition, mappedBetType, callType } = options;
  
  // Debug log to help trace issues
  console.log('formatTitle inputs:', { position, betType, openPosition, threeBetPosition, callType });
  
  if (!position || !betType) {
    return 'Range';
  }

  // Handle SB Limp scenarios
  if (betType === 'SB_LIMP' || betType === 'LIMP' ||
      betType.includes('SB_LIMP') || betType.includes('LIMP')) {
    return `${position} Limp`;
  }

  // Order matters - check more specific patterns first

  // Handle FACING_OPEN_CALL before checking just OPEN
  if (betType === 'FACING_OPEN_CALL' || betType.includes('FACING_OPEN_CALL')) {
    return openPosition
      ? `${position} Call vs ${openPosition} Open`
      : `${position} Call vs Open`;
  }

  // Handle OPEN by itself
  if (betType === 'OPEN') {
    return `${position} Open`;
  }

  // Handle 3bet call scenarios
  if (betType === 'FACING_3BET_CALL' || betType.includes('FACING_3BET_CALL')) {
    return threeBetPosition
      ? `${position} Call vs ${threeBetPosition} 3Bet`
      : `${position} Call vs 3Bet`;
  }

  // Handle 4bet scenarios
  if (betType === 'FACING_3BET_4BET' || betType.includes('FACING_3BET_4BET')) {
    return threeBetPosition
      ? `${position} 4Bet vs ${threeBetPosition} 3Bet`
      : `${position} 4Bet`;
  }

  // Handle 3bet scenarios
  if (betType === 'FACING_OPEN_3BET' || betType.includes('FACING_OPEN_3BET')) {
    return openPosition
      ? `${position} 3Bet vs ${openPosition} Open`
      : `${position} 3Bet`;
  }

  // Fallback for other bet types
  if (mappedBetType) {
    return `${position} ${mappedBetType}`;
  }

  // Format the betType string nicely as a last resort
  const action = betType.toLowerCase()
    .replace('facing_', '')
    .replace('_', ' ')
    .replace('3bet', '3Bet')
    .replace('4bet', '4Bet');

  return `${position} ${action}`;
};

/**
 * Maps a position to its display name
 * 
 * @param {string} position - Position code (e.g., "BTN", "BB")
 * @returns {string} Display name for the position
 */
export const mapPositionToDisplay = (position) => {
  const positionMap = {
    'SB': 'Small Blind',
    'BB': 'Big Blind',
    'UTG': 'Under the Gun',
    'MP': 'Middle Position',
    'CO': 'Cutoff',
    'BTN': 'Button',
    'LJ': 'Lojack',
    'HJ': 'Hijack'
  };

  return positionMap[position] || position;
};

/**
 * Gets the appropriate action text for a bet type
 * 
 * @param {string} betType - Type of betting action
 * @returns {string} Action text (e.g., "OPEN", "3BET", "CALL")
 */
export const getActionTypeForBetType = (betType) => {
  switch (betType) {
    case 'OPEN':
      return 'OPEN';
    case 'FACING_OPEN_CALL':
      return 'CALL';
    case 'FACING_OPEN_3BET':
      return '3BET';
    case 'FACING_3BET_CALL':
      return 'CALL';
    case 'FACING_3BET_4BET':
      return '4BET';
    case 'FACING_4BET_CALL':
      return 'CALL';
    case 'SQUEEZE_4BET':
      return '4BET';
    case 'CALL_VS_3BET':
      return 'CALL';
    default:
      return betType;
  }
};